import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';

const UarShoppingCartModal = ({
	open,
	onClose,
	cartItems,
	onClear,
	onRemove,
	onSubmit,
	isSubmitting,
}) => {
	const groupedItems = cartItems.reduce((grouped, item) => {
		const population = item.populationLabel || 'Other';
		const staffGroupKey = `${population}:${item.staffMemberId}`;
		if (!grouped[staffGroupKey]) {
			grouped[staffGroupKey] = {
				population,
				staffMemberName: item.staffMemberName,
				staffMemberId: item.staffMemberId,
				items: [],
			};
		}
		grouped[staffGroupKey].items.push(item);
		return grouped;
	}, {});

	const actionColor = action => {
		if (action === 'MODIFY') {
			return 'warning';
		}

		if (action === 'REVOKE') {
			return 'error';
		}

		return 'info';
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth='sm'
			sx={{
				'& .MuiDialog-paper': {
					width: '100%',
					maxWidth: 450,
					m: 2,
					maxHeight: '80vh',
				},
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					pb: 1,
				}}
			>
				<Typography variant='h6'>Shopping Cart</Typography>
				<IconButton onClick={onClose} size='small'>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ py: 1 }}>
				<Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
					The following actions are queued and will be submitted together.
				</Typography>
				<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
					You will have a chance to review all actions in detail before final submission.
				</Typography>
				<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
					<b>Revoke</b> actions will happen immediately after submission, while <b>Modify</b> actions will be sent for approval before taking effect.
				</Typography>
				<Stack direction='row' justifyContent='flex-end' sx={{ mb: 1.5 }}>
					<Button
						size='small'
						variant='text'
						disabled={cartItems.length === 0}
						onClick={onClear}
					>
						Clear All
					</Button>
				</Stack>

				<Divider />

				<Box
					sx={{
						mt: 1,
						overflowY: 'auto',
						flex: 1,
						maxHeight: 'calc(80vh - 200px)',
					}}
				>
					{cartItems.length === 0 ? (
						<Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
							Add Modify or Revoke actions from any app row to build your shopping cart.
						</Typography>
					) : (
						Object.values(groupedItems).map(group => {
							return (
								<Box key={`${group.population}:${group.staffMemberId}`} sx={{ mb: 2 }}>
									<Stack
										direction='row'
										alignItems='center'
										justifyContent='space-between'
										sx={{ mb: 0.5 }}
									>
										<Typography variant='subtitle2'>
											{group.staffMemberName}
										</Typography>
										<Chip label={group.population} size='small' variant='outlined' />
									</Stack>
									<List dense disablePadding>
										{group.items.map(item => {
											return (
												<ListItem
													key={item.itemKey}
													secondaryAction={
														<IconButton
															size='small'
															onClick={() => onRemove(item.itemKey)}
															aria-label='remove cart item'
														>
															<DeleteOutlineIcon fontSize='inherit' />
														</IconButton>
													}
												>
													<ListItemText
														primary={item.appName}
														secondary={
															<Stack direction='row' spacing={1} alignItems='center'>
																<Chip
																	size='small'
																	color={actionColor(item.action)}
																	label={item.action}
																/>
																<Typography variant='caption'>
																	{item.username || 'No username'}
																</Typography>
															</Stack>
														}
													/>
												</ListItem>
											);
										})}
									</List>
									<Divider sx={{ mt: 1 }} />
								</Box>
							);
						})
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ p: 1.5, gap: 1 }}>
				<Button size='small' onClick={onClose}>
					Close
				</Button>
				<Button
					size='small'
					variant='contained'
					disabled={cartItems.length === 0 || isSubmitting}
					onClick={onSubmit}
				>
					{isSubmitting ? 'Creating...' : 'Create Requests'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

UarShoppingCartModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
	onClear: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool,
};

UarShoppingCartModal.defaultProps = {
	isSubmitting: false,
};

export default UarShoppingCartModal;
